import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent } from "../../../components/ui/tabs";
import {
  PtsPillTabsList,
  PtsPillTabsTrigger,
} from "../../../components/PtsPillTabs";
import { LayoutDashboard, Settings2, Wallet } from "lucide-react";
import BillingConfigTab from "./BillingConfigTab";
import DashboardTab from "./DashboardTab";
import InvoiceReminderCard from "./InvoiceReminderCard";
import PaymentStatusTab from "./PaymentStatusTab";

type BillingInvoiceTab = "dashboard" | "config" | "payment";

function resolveBillingInvoiceTab(value: string | null): BillingInvoiceTab {
  if (value === "config" || value === "payment" || value === "dashboard") {
    return value;
  }
  return "dashboard";
}

const BillingInvoicePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveBillingInvoiceTab(searchParams.get("tab"));
  const editBillingId = searchParams.get("editBilling")?.trim() ?? undefined;
  const editNonce = searchParams.get("editNonce")?.trim() ?? undefined;

  const handleTabChange = (value: string) => {
    const nextTab = resolveBillingInvoiceTab(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", nextTab);
      if (nextTab !== "config") {
        next.delete("editBilling");
        next.delete("editNonce");
      }
      return next;
    });
  };

  const openReminderInConfig = useMemo(
    () => (billingId: string) => {
      setSearchParams({
        tab: "config",
        editBilling: billingId,
        editNonce: String(Date.now()),
      });
    },
    [setSearchParams]
  );

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="flex-1">
          <h1 className="text-primary text-xl font-bold md:text-2xl">
            Billing Invoice
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage billing configurations, invoices, and payment tracking
          </p>
        </div>

        <div className="w-full md:w-auto md:shrink-0">
          <InvoiceReminderCard onOpenReminder={openReminderInConfig} />
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <div className="sticky top-[-1.9rem] z-30 bg-gray-50/95 pb-4 pt-[1.5rem] backdrop-blur-sm">
          <PtsPillTabsList
            className="overflow-x-auto md:overflow-x-visible"
            listClassName="w-max min-w-full md:w-full"
          >
            <PtsPillTabsTrigger
              value="dashboard"
              className="flex-none whitespace-nowrap md:flex-1"
            >
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </PtsPillTabsTrigger>
            <PtsPillTabsTrigger
              value="config"
              className="flex-none whitespace-nowrap md:flex-1"
            >
              <Settings2 className="h-4 w-4" />
              Billing Configuration
            </PtsPillTabsTrigger>
            <PtsPillTabsTrigger
              value="payment"
              className="flex-none whitespace-nowrap md:flex-1"
            >
              <Wallet className="h-4 w-4" />
              Payment Status
            </PtsPillTabsTrigger>
          </PtsPillTabsList>
        </div>
        <TabsContent value="dashboard">
          <DashboardTab />
        </TabsContent>
        <TabsContent value="config">
          <BillingConfigTab
            editBillingId={editBillingId}
            editNonce={editNonce}
          />
        </TabsContent>
        <TabsContent value="payment">
          <PaymentStatusTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingInvoicePage;
